import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = 'button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),summary,video[controls],[tabindex]:not([tabindex="-1"])';

/** Own focus and Escape while a modal is open; restore the previous page state. */
export function useDialog(ref: RefObject<HTMLElement | null>, open: boolean, close: () => void, modal = true): void {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    const dialog = ref.current;
    if (!open || !dialog) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const background: { element: HTMLElement; inert: boolean }[] = [];
    // Isolate siblings at each level, never an ancestor containing the dialog.
    if (modal) for (let branch: HTMLElement = dialog; branch.parentElement; branch = branch.parentElement) {
      for (const sibling of branch.parentElement.children) {
        if (sibling !== branch && sibling instanceof HTMLElement) {
          background.push({ element: sibling, inert: sibling.inert });
          sibling.inert = true;
        }
      }
      if (branch.parentElement === document.body) break;
    }
    const overflow = document.body.style.overflow;
    if (modal) document.body.style.overflow = 'hidden';
    const controls = () => Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(el => {
      if (el.closest('[hidden], [inert]') || el.tabIndex < 0) return false;
      for (let node: HTMLElement | null = el; node && node !== dialog; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        const details = node.parentElement;
        if (details instanceof HTMLDetailsElement && !details.open && node.tagName !== 'SUMMARY') return false;
      }
      return true;
    });
    const oldTabIndex = dialog.getAttribute('tabindex');
    dialog.tabIndex = -1;
    (controls()[0] ?? dialog).focus();
    const onKey = (event: KeyboardEvent) => {
      if (!modal && (!dialog.contains(document.activeElement) || document.querySelector('[aria-modal="true"]'))) return;
      if (event.key === 'Escape') {
        event.preventDefault(); event.stopImmediatePropagation(); closeRef.current();
      } else if (modal && event.key === 'Tab') {
        const items = controls(), first = items[0], last = items.at(-1);
        if (!first) { event.preventDefault(); dialog.focus(); }
        else if (!dialog.contains(document.activeElement) || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault(); (event.shiftKey ? last : first)?.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      background.forEach(({ element, inert }) => { element.inert = inert; });
      document.body.style.overflow = overflow;
      if (oldTabIndex === null) dialog.removeAttribute('tabindex'); else dialog.setAttribute('tabindex', oldTabIndex);
      if (opener?.isConnected && opener !== document.body) opener.focus();
      else document.querySelector<HTMLElement>('.topbar-more-btn')?.focus();
    };
  }, [open, ref, modal]);
}
